import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert } from "@mui/material";
import { toast } from "react-toastify";
import { useMicroPractices } from "../../hooks/useRedux";
import MicroPracticeForm from "./MicroPracticeForm";
import { MicroPractice } from "../../store/slices/types";

const EditMicroPractice: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { microPractices, updateMicroPractice, loading } = useMicroPractices();

  const microPractice = microPractices.find(
    (mp) => (mp._id || mp.id) === id
  ) as MicroPractice | undefined;

  const handleSave = async (microPracticeData: Partial<MicroPractice>) => {
    if (!id) {
      toast.error("Invalid micro practice ID");
      return;
    }

    try {
      await updateMicroPractice(id, microPracticeData);
      toast.success("micro practice is updated");
      navigate("/micro-practices");
    } catch (error) {
      console.error("Failed to update micro practice:", error);
      toast.error(
        `❌ Failed to update micro practice: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleCancel = () => {
    navigate("/micro-practices");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!microPractice) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          Micro Practice not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          mb: 4,
        }}
      >
        <Typography variant='h4' fontWeight='700' gutterBottom>
          Edit Micro Practice
        </Typography>
        <Typography variant='body1' sx={{ opacity: 0.9 }}>
          Update your wellness practice
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <MicroPracticeForm
          microPractice={microPractice}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </Paper>
    </Box>
  );
};

export default EditMicroPractice;
